import { useEffect, useState, type FormEvent } from 'react';
import {
  DisplayName,
  GENDERS,
  ORIGINS,
  PRONOUNS,
  STARTING_SITUATIONS,
  UK_HOME_REGIONS,
  USERNAME_MESSAGES,
  UsernameChange,
  formatPence,
  type AccountView,
  type LifeView,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { toPreviewCharacter } from '@london-life/game/appearance';
import { api } from '../lib/api';
import { shareLink } from '../lib/share';
import { AvatarPreview } from '../world';
import { LookEditor } from './look-editor';

const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const countryName = (code: string) => new Intl.DisplayNames(['en-GB'], { type: 'region' }).of(code) ?? code;

// one editable name: shows the value, edits in place, saves through the server
function NameField({
  label,
  value,
  prefix,
  hint,
  locked,
  validate,
  onSave,
}: {
  label: string;
  value: string;
  prefix?: string;
  hint: string;
  locked?: string | null;
  validate: (value: string) => string | null;
  onSave: (value: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  useEffect(() => setDraft(value), [value]);
  const problem = draft === value ? null : validate(draft);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (problem || draft === value) return setEditing(false);
    setBusy(true);
    setFailure(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="profile-field">
      <span className="profile-label">{label}</span>
      {!editing ? (
        <div className="profile-value">
          <strong>
            {prefix}
            {value || '—'}
          </strong>
          <Button type="button" variant="outline" size="sm" disabled={Boolean(locked)} onClick={() => setEditing(true)}>
            Change
          </Button>
        </div>
      ) : (
        <form className="profile-value" onSubmit={(event) => void submit(event)}>
          <Input aria-label={label} value={draft} onChange={(event) => setDraft(event.target.value)} autoFocus />
          <Button type="submit" size="sm" disabled={busy || Boolean(problem)}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
        </form>
      )}
      <small className="profile-hint">{locked ?? (editing && problem ? problem : hint)}</small>
      {failure && <ErrorNotice error={failure} />}
    </div>
  );
}

export function ProfileTab({
  life,
  account,
  onAccount,
  onLife,
}: {
  life: LifeView;
  account: AccountView;
  onAccount: (account: AccountView) => void;
  onLife: (life: LifeView, message?: string) => void;
}) {
  const [editingLook, setEditingLook] = useState(false);
  const [shared, setShared] = useState<string | null>(null);
  if (editingLook)
    return (
      <LookEditor
        life={life}
        onDone={(updated, message) => {
          setEditingLook(false);
          if (updated) onLife(updated, message);
        }}
      />
    );
  const story = life.story;
  const from = story.origin === 'uk_born' ? UK_HOME_REGIONS[story.homeRegion] : countryName(story.country);
  const share = async () => {
    const url = `${window.location.origin}/`;
    const text = `I'm living in London as ${life.firstName}${account.username ? ` (@${account.username})` : ''}. Come and play London Life.`;
    if ((await shareLink({ title: 'London Life', text, url })) === 'copied')
      setShared('Link copied. Paste it to a friend.');
  };
  return (
    <div className="profile-grid">
      <section className="profile-card profile-hero" aria-label="Your character">
        <div className="profile-avatar">
          <AvatarPreview character={toPreviewCharacter(life.appearance, life.firstName)} />
        </div>
        <div>
          <p className="profile-facts">
            {GENDERS[life.identity.gender]} · {PRONOUNS[life.identity.pronouns]}
          </p>
          <p className="profile-facts">
            {ORIGINS[story.origin]}, from {from}
            {story.origin === 'uk_born' && story.heritage ? ` (${countryName(story.heritage)} heritage)` : ''}
          </p>
          <p className="profile-facts">{STARTING_SITUATIONS[story.situation]}</p>
          <p className="profile-facts">
            In London since {longDate(life.createdAt)} · Wallet <strong>{formatPence(life.walletPence)}</strong>
          </p>
          <Button type="button" onClick={() => setEditingLook(true)}>
            Change your look
          </Button>
        </div>
      </section>

      <section className="profile-card" aria-labelledby="profile-names">
        <h3 id="profile-names">Names</h3>
        <NameField
          label="Display name"
          value={account.displayName ?? ''}
          hint="Shown to other players. Change it any time."
          validate={(value) => {
            const check = DisplayName.safeParse(value);
            return check.success ? null : (check.error.issues[0]?.message ?? 'Choose another name');
          }}
          onSave={async (displayName) => onAccount((await api.account.displayName({ displayName })).account)}
        />
        <NameField
          label="Username"
          prefix="@"
          value={account.username ?? ''}
          hint="Your unique @name. You can change it once every 30 days; your old one stays reserved for you for 30 days."
          locked={
            account.usernameChangeFrom
              ? `You can change your @name again from ${longDate(account.usernameChangeFrom)}.`
              : null
          }
          validate={(value) =>
            UsernameChange.safeParse({ username: value }).success ? null : USERNAME_MESSAGES.format
          }
          onSave={async (username) => {
            const free = await api.profile.usernameAvailability(username);
            if (!free.available) throw new Error(USERNAME_MESSAGES[free.reason ?? 'taken']);
            onAccount((await api.account.username({ username })).account);
          }}
        />
        <div className="profile-field">
          <span className="profile-label">Email</span>
          <div className="profile-value">
            <strong>{account.email ?? '—'}</strong>
            <span className={account.emailVerified ? 'profile-badge' : 'profile-badge warn'}>
              {account.emailVerified ? 'Verified' : 'Not verified'}
            </span>
          </div>
          <small className="profile-hint">
            Used to sign in and for account messages. Never shown to other players.
          </small>
        </div>
      </section>

      <section className="profile-card" aria-labelledby="profile-invite">
        <h3 id="profile-invite">Invite friends</h3>
        <p className="profile-hint">London is better with people you know. Share the game with a friend.</p>
        <Button type="button" variant="outline" onClick={() => void share()}>
          Share London Life
        </Button>
        {shared && (
          <p role="status" className="profile-hint">
            {shared}
          </p>
        )}
      </section>
    </div>
  );
}
